import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Alert from '@mui/material/Alert'
import { Car } from 'lucide-react'
import { sendPasswordResetEmail } from 'firebase/auth'
import { auth } from '../../config/firebase'
import { loginPageStyles } from './LoginPage.styles'

function ResetPasswordPage() {
    const { t } = useTranslation()
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [sent, setSent] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)
        try {
            await sendPasswordResetEmail(auth, email)
            setSent(true)
        } catch (err) {
            console.error('Password reset failed:', err)
            setError(t('auth.resetPassword.failed'))
        } finally {
            setLoading(false)
        }
    }

    return (
        <Container maxWidth="xs" sx={loginPageStyles.container}>
            <Box sx={loginPageStyles.logoWrapper}>
                <Box sx={loginPageStyles.logoIcon}>
                    <Car size={28} color="white" />
                </Box>
                <Typography variant="h5" fontWeight={700} textAlign="center">
                    {t('auth.resetPassword.title')}
                </Typography>
            </Box>

            <Paper elevation={0} sx={loginPageStyles.paper}>
                {sent ? (
                    <>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {t('auth.resetPassword.sent')} <strong>{email}</strong>.
                        </Alert>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            <Link component={RouterLink} to="/login" fontWeight={600}>
                                {t('auth.backToLogin')}
                            </Link>
                        </Typography>
                    </>
                ) : (
                    <>
                        {error && <Alert severity="error" sx={loginPageStyles.errorAlert}>{error}</Alert>}
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            {t('auth.resetPassword.description')}
                        </Typography>
                        <Box component="form" onSubmit={handleSubmit} sx={loginPageStyles.form}>
                            <TextField
                                fullWidth
                                label={t('common.email')}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={loading}
                            >
                                {loading ? t('auth.resetPassword.sending') : t('auth.resetPassword.button')}
                            </Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
                            <Link component={RouterLink} to="/login" fontWeight={600}>
                                {t('auth.backToLogin')}
                            </Link>
                        </Typography>
                    </>
                )}
            </Paper>
        </Container>
    )
}

export default ResetPasswordPage
